import type { FC, ReactNode } from 'react'

import { useI18n } from '@/i18n/context'
import { Field, TextInput } from '@/components/primitives/field'
import { NationalitySelect, PhoneField } from '@/components/forms/country-fields'
import { isName, parsePhone } from '@/lib/validation'

export interface ProfileValues {
    firstName: string
    lastName: string
    nationality: string
    phoneCountry: string
    phoneNumber: string
}

export type ProfileErrors = Partial<Record<keyof ProfileValues | 'phone', string>>

export const emptyProfile: ProfileValues = { firstName: '', lastName: '', nationality: '', phoneCountry: '', phoneNumber: '' }

// Validates the shared personal details and returns translated messages per field.
export const validateProfile = (values: ProfileValues, errors: {
    required: string
    invalidName: string
    invalidNationality: string
    phoneCountry: string
    invalidPhone: string
}): ProfileErrors => {
    const result: ProfileErrors = {}

    if (!values.firstName.trim()) result.firstName = errors.required
    else if (!isName(values.firstName)) result.firstName = errors.invalidName

    if (!values.lastName.trim()) result.lastName = errors.required
    else if (!isName(values.lastName)) result.lastName = errors.invalidName

    if (!values.nationality) result.nationality = errors.required

    const phone = parsePhone({ country: values.phoneCountry as never, number: values.phoneNumber })

    if (!phone.ok) result.phone = phone.reason === 'missing' ? errors.required : phone.reason === 'country' ? errors.phoneCountry : errors.invalidPhone

    return result
}

interface ProfileFieldsProps {
    idPrefix: string
    values: ProfileValues
    errors: ProfileErrors
    onChange: (values: ProfileValues, changed: keyof ProfileErrors) => void
}

export const ProfileFields: FC<ProfileFieldsProps> = ({ idPrefix, values, errors, onChange }): ReactNode => {
    const { m } = useI18n()

    const set = (patch: Partial<ProfileValues>, changed: keyof ProfileErrors) => onChange({ ...values, ...patch }, changed)

    return (
        <div className='grid gap-4 sm:grid-cols-2'>
            <Field id={`${idPrefix}-first-name`} label={m.auth.firstName} error={errors.firstName}>
                <TextInput
                    id={`${idPrefix}-first-name`}
                    value={values.firstName}
                    invalid={Boolean(errors.firstName)}
                    autoComplete='given-name'
                    onChange={event => set({ firstName: event.target.value }, 'firstName')}
                />
            </Field>

            <Field id={`${idPrefix}-last-name`} label={m.auth.lastName} error={errors.lastName}>
                <TextInput
                    id={`${idPrefix}-last-name`}
                    value={values.lastName}
                    invalid={Boolean(errors.lastName)}
                    autoComplete='family-name'
                    onChange={event => set({ lastName: event.target.value }, 'lastName')}
                />
            </Field>

            <Field id={`${idPrefix}-nationality`} label={m.auth.nationality} error={errors.nationality} showIcon={false} className='sm:col-span-2'>
                <NationalitySelect
                    id={`${idPrefix}-nationality`}
                    value={values.nationality}
                    invalid={Boolean(errors.nationality)}
                    placeholder={m.auth.selectNationality}
                    onChange={nationality => set({ nationality, phoneCountry: values.phoneCountry || nationality }, 'nationality')}
                />
            </Field>

            <Field id={`${idPrefix}-phone`} label={m.auth.phone} error={errors.phone} showIcon={false} className='sm:col-span-2'>
                <PhoneField
                    id={`${idPrefix}-phone`}
                    country={values.phoneCountry}
                    number={values.phoneNumber}
                    invalid={Boolean(errors.phone)}
                    codeLabel={m.auth.countryCode}
                    codePlaceholder={m.auth.selectCountryCode}
                    numberPlaceholder={m.auth.phonePlaceholder}
                    onChange={phone => set({ phoneCountry: phone.country, phoneNumber: phone.number }, 'phone')}
                />
            </Field>
        </div>
    )
}
